import { Node } from "../Node";
import { PBoolean } from "../properties/PBoolean";
import { PEuler } from "../properties/PEuler";
import { PColor } from "../properties/PColor";
import { PImage } from "../properties/PImage";
import { PModel3d } from "../properties/PModel3d";
import { PString } from "../properties/PString";
import { PVector2 } from "../properties/PVector2";
import { PVector3 } from "../properties/PVector3";
import { PNumber } from "../properties/PNumber";
import { PJson } from "../properties/PJson";
import { assertNotNull, json, MetadataRow } from "../../global";
import { PForeignKey } from "../properties/PForeignKey";
import { PMap } from "../properties/PMap";
import { PNodeListMeta } from "../properties/PNodeListMeta";

const typeMap = {
  Bool: PBoolean,
  Color: PColor,
  Euler: PEuler,
  Float: PNumber,
  Id: PNumber,
  Image: PImage,
  Json: PJson,
  Map: PMap,
  Model3D: PModel3d,
  Rotation: PEuler,
  String: PString,
  Timestamp: PString,
  Vector2: PVector2,
  Vector3: PVector3,
};

export type GenericMetaNodeConstructor = new (
  metaTable: json,
  meta: MetadataRow,
) => GenericMetaNode;

export class GenericMetaNode extends Node {
  metaTable: json;
  meta: MetadataRow;
  id: number;

  constructor(metaTable: json, meta: MetadataRow) {
    super();
    this.metaTable = metaTable;
    this.meta = meta;
    assertNotNull("metaTable", this.metaTable);
    assertNotNull("meta", this.meta);
  }

  init(): this {
    super.init();
    this.fromMetadataRow(this.meta);
    return this;
  }

  getTableName() {
    return this.metaTable["name"];
  }

  createProperties() {
    return (
      this.metaTable["columns"]
        // .filter(column=>!/^(id|created_at|updated_at)$/g.test(column.name))
        .map((column) => {
          let propertyClass;

          const o = { ...column, node: this };

          const maybeSetSelectOptions = () => {
            if (o.selectOptions && !_.isArray(o.selectOptions)) {
              // fixme dirty hack, weil manchmal selectOptions vom Server als [0=>"..",...] geliefert wird statt ["..",...]
              o.selectOptions = _.values(o.selectOptions);
            }
          };

          if (column.foreignKey) {
            propertyClass = PForeignKey;
            maybeSetSelectOptions();
            // if (APP.richMetadata._fkNeverResolve(column) || o.foreignKeyParsed.length!=1) {
            //     propertyClass = PForeignKey;
            //     maybeSetSelectOptions();
            // } else {
            //     propertyClass = PNodeRefForeignKey;
            //     o.column = column;
            //     o.metaTable = o.foreignKeyParsed[0];
            // }
          } else if (column.type == "Backref") {
            propertyClass = PNodeListMeta;
            o.metaTable = column.table;
            o.rows = this.meta[o.name];
            o.canModifyList = false;
          } else {
            propertyClass = typeMap[column.type];
            if (!propertyClass) {
              console.error("Unknown type " + column.type);
              return null;
              // throw new Error('Unknown type '+column.type);
            }
            maybeSetSelectOptions();
          }

          delete o.type;
          if (/^(id|created_at|updated_at)$/.test(o.name)) {
            o.showInEditor = () => false;
          }
          return new propertyClass(o);
        })
        .filter((p) => p)
    );
  }

  saveToServer(): Promise<boolean | void> {
    const metadata = this.toMetadataRow();
    return APP.wkfgBackendApi
      .saveMetadata(this.getTableName(), metadata.id == null ? "create" : "update", metadata)
      .then((metadata) => {
        this.id = metadata.id;
        this.updateMeta();
      });
  }

  removeOnServer(): Promise<boolean> {
    if (this.id == null) {
      return Promise.resolve(null);
    }
    return APP.wkfgBackendApi.deleteMetadata(this.getTableName(), this.id, true);
  }

  fromMetadataRow(row: MetadataRow) {
    this.getProperties()
      .filter((property) => property.type && property.metadataPersistent !== false)
      .forEach((property) => property.fromMetadataRow(row));
    return this;
  }

  toMetadataRow(): MetadataRow {
    const row = {
      id: this["id"],
    };
    this.getProperties()
      .filter((property) => property.type && property.metadataPersistent !== false)
      .forEach((property) => property.toMetadataRow(row));
    return row;
  }

  updateMeta() {
    Object.assign(this.meta, this._resolveForeignKeysByReferences(this.toMetadataRow()));
  }

  _resolveForeignKeysByReferences(row) {
    // ist eine modifzierte Kopie aus ValidationMetadata. Die Backreferenzen werden allerdings nicht aufgelöst
    const fkColumns = this.metaTable["columns"].filter(
      (column) => column.foreignKey != null && !APP.richMetadata._fkNeverResolve(column),
    );
    for (const column of fkColumns) {
      const isOne2Many = column.multiselect || column.variantKey;
      const mapFunction = isOne2Many
        ? (array, callback) =>
            array.map(callback).filter((string) => !(string == null || string === ""))
        : (string, callback) => (string == null || string === "" ? null : callback(string));
      const resolveEntityOrString = (key) => column._foreignKeyCache.get(key);
      row[column.name] = mapFunction(row[column.name], resolveEntityOrString);
    }
    return row;
  }
}
